import { h, FunctionComponent } from 'preact'
import { useEffect, useRef, useState, useCallback } from 'preact/hooks'
import { loadHuman, faceDetection } from './FaceDetection'
import { DebugUI } from './DebugUI'
import { VideoFrame } from './VideoFrame'
import Webcam from 'react-webcam'
import { classy, getTranslation } from '../helpers'
import styles from './video.module.scss'
import { options } from '../types'
import { LivenessError } from '../VideoCapture/errors'

interface Props {
  debug: boolean
  landscape: boolean
  setVideoPayload: (arg0: Blob) => void
  onError: (error: LivenessError) => void
  options: options
}

export interface BoundingBox {
  left: number
  top: number
  width: number
  height: number
}

export const VideoFeedback: FunctionComponent<Props> = ({
  debug,
  landscape,
  setVideoPayload,
  onError,
  options,
}: Props) => {
  const videoRef = useRef<any>(null)
  const { language } = options || { language: 'en_US' }
  const mediaRecorderRef = useRef<any>(null)
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([])
  const [yaw, setYaw] = useState(0)

  const constraints = {
    width: 640,
    height: 480,
    frameRate: 25,
    facingMode: 'user',
  }
  const faceOverflow = 40
  const [scanComplete, setScanComplete] = useState(false)
  const [ready, setReady] = useState(false)
  const [centerBounds, setCenterBounds] = useState<BoundingBox>({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  })
  const [faceBounds, setFaceBounds] = useState<BoundingBox>({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
  })
  /*
  *
    Context for the presented solution on facebounds and centerbounds:
    Both the centerbounds and facebounds are calculated using a matrix-scheme, meaning that the box that defines either of them is split into sections.
    For centerbounds (the optimal bounding area), the square is split into a 5 x (ready ? 16 : 10) matrix from the original video resolution.
    This means that, before the face is detected (i.e. ready == true), the optimal area for the face to be detected in starts at 1/5 of the original resolution from the left up to 4/5, and 1/16 from the top to 15/16. The height changes on face being detected due to the optimal area expanding to leave room for the user to do the head turning.
  *
  */
  const [faceIsCentered, setFaceIsCentered] = useState(false)
  const [faceObject, setFaceObject] = useState<any>(null)
  const [warningMessage, setWarningMessage] = useState('')
  const [instructionMessage, setInstructionMessage] = useState(
    getTranslation('avc_face_alignment.title', language)
  )

  let freshStart = true
  let lastUpdate = 0
  let noFaceDetected = false

  const handleDataAvailable = useCallback(
    ({ data }: any) => {
      if (data.size > 0) {
        setRecordedChunks((prev: Blob[]) => [...prev, data])
      }
    },
    [setRecordedChunks]
  )

  const startCapture = useCallback(() => {
    if (videoRef.current.video) {
      mediaRecorderRef.current = new MediaRecorder(
        videoRef?.current.video.srcObject,
        {
          mimeType: 'video/webm',
        }
      )
      mediaRecorderRef.current.ondataavailable = handleDataAvailable
      mediaRecorderRef.current.start()
    }
  }, [videoRef, mediaRecorderRef])

  const stopCapture = useCallback(() => {
    mediaRecorderRef.current.stop()
  }, [mediaRecorderRef, videoRef])

  const setCenterConstraints = (): void => {
    const videoReference = videoRef.current.video
    if (!videoReference) return

    const video = videoReference?.getBoundingClientRect()
    const minX = video.width / 5
    const minY = video.height / (ready ? 16 : 10)
    const overflowX = ready ? 20 : 0
    const newCenterBounds = {
      left: minX - overflowX,
      top: minY,
      width: video.width - 2 * minX + 2 * overflowX,
      height: video.height - 3 * minY,
    }
    setCenterBounds((prevBounds) => ({ ...prevBounds, ...newCenterBounds }))
  }

  const detectFaces = async function (timestamp: number): Promise<void> {
    const seconds = Math.trunc(timestamp / 1000)

    if (freshStart) {
      lastUpdate = seconds
      noFaceDetected = false
      freshStart = false
    }

    // select input HTMLVideoElement and output HTMLCanvasElement from page
    const video = videoRef.current.video
    if (video) {
      faceDetection(video).then((result) => {
        if (result.face[0]?.mesh.length > 0) {
          setYaw(Math.round(result.face[0].rotation!.angle.yaw * 100) / 100)
          setFaceObject(result.face[0])
          freshStart = true
        } else if (!scanComplete && seconds - lastUpdate >= 5) {
          if (noFaceDetected) {
            setWarningMessage('')
            onError(LivenessError.FACE_DETECTION_TIMEOUT)
            return
          }

          setWarningMessage(
            getTranslation(
              'avc_face_alignment.feedback_no_face_detected',
              language
            )
          )

          lastUpdate = seconds
          noFaceDetected = true
        }
        requestAnimationFrame(detectFaces)
      })
    }
  }

  const checkDepth = (): string => {
    if (faceBounds.width > 0) {
      const boxWidth = faceBounds.width
      const maxWidth = 300
      const minWidth = ready ? 187 : 225
      if (!scanComplete) {
        if (boxWidth > maxWidth) return 'Move back'
        else if (boxWidth < minWidth) return 'Move closer'
      }
    }
    return ''
  }

  const setFaceConstraints = (): boolean => {
    const videoReference = videoRef.current.video
    if (!videoReference) return false

    const videoResProportion =
      videoReference.getBoundingClientRect().height / constraints.height
    const newFaceBounds = {
      left: (faceObject.box[0] + faceOverflow) * videoResProportion,
      top: (faceObject.box[1] + faceOverflow / 2) * videoResProportion,
      width: (faceObject.box[2] - 2 * faceOverflow) * videoResProportion,
      height: (faceObject.box[3] - faceOverflow) * videoResProportion,
    }

    setFaceBounds((prevBounds) => ({ ...prevBounds, ...newFaceBounds }))
    return true
  }

  useEffect(() => {
    if (recordedChunks[0]?.size > 0) {
      const blob = new Blob(recordedChunks, {
        type: 'video/webm',
      })

      setVideoPayload(blob)
    }
  }, [recordedChunks])

  useEffect(() => {
    if (ready) startCapture()
    setCenterConstraints()
  }, [ready])

  useEffect(() => {
    if (scanComplete) {
      setTimeout(() => stopCapture(), 2000)
    }
  }, [scanComplete])

  useEffect(() => {
    if (faceObject) {
      setFaceConstraints()
      if (!scanComplete) {
        if (!faceIsCentered) {
          setWarningMessage(
            getTranslation('avc_face_alignment.feedback_not_centered', language)
          )
        } else if (checkDepth().length) {
          setWarningMessage(checkDepth())
        } else {
          setWarningMessage('')
          setInstructionMessage(
            getTranslation('avc_face_capture.title', language)
          )
          setReady(true)
        }
      }
    }
  }, [faceObject])

  useEffect(() => {
    setFaceIsCentered(
      faceBounds.left > centerBounds.left &&
        faceBounds.top > centerBounds.top &&
        faceBounds.left + faceBounds.width <
          centerBounds.left + centerBounds.width &&
        faceBounds.top + faceBounds.height <
          centerBounds.top + centerBounds.height
    )
  }, [faceBounds])

  useEffect(() => {
    const video = videoRef.current.video
    loadHuman()
    if (video) {
      freshStart = true
      setCenterConstraints()
      requestAnimationFrame(detectFaces)
    }
  }, [])

  return (
    <div className={styles.videoContainer}>
      {landscape && <div className={styles.hzOverflow} />}
      {debug && (
        <DebugUI
          faceYaw={yaw}
          centerBounds={centerBounds}
          faceBounds={faceBounds}
          warningMessage={warningMessage}
          portrait={!landscape}
          videoWidth={videoRef?.current?.video.getBoundingClientRect().width}
        />
      )}
      <VideoFrame
        faceYaw={yaw}
        ready={ready}
        setScanComplete={setScanComplete}
        scanComplete={scanComplete}
        warningMessage={warningMessage}
        instructionMessage={instructionMessage}
      />
      <div className={styles.videoElementWrapper}>
        <Webcam
          className={classy(
            styles.videoElement,
            landscape ? styles.landscapeWrapper : null
          )}
          ref={videoRef}
          videoConstraints={constraints}
        />
      </div>
      {landscape && <div className={styles.hzOverflow} />}
    </div>
  )
}
